import { useEffect, useId, useRef, type ReactNode } from 'react';
import { CloseIcon } from './icons';
import styles from './Modal.module.css';

interface ModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  width?: string;
}

export function Modal({ open, onClose, title, children, width = '480px' }: ModalProps) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const titleId = useId();

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    if (open && !dialog.open) dialog.showModal();
    else if (!open && dialog.open) dialog.close();
  }, [open]);

  // The dialog's native close event (Escape/cancel, form method="dialog",
  // browser-initiated) syncs React state via onClose below — same pattern as
  // BookmarkPreview, so `open` can never desync from `dialog.open`.
  return (
    <dialog ref={dialogRef} className={styles.dialog} aria-labelledby={titleId} onClose={onClose} onClick={(e) => {
      if (e.target === e.currentTarget) onClose();
    }}>
      <div className={styles.panel} style={{ maxWidth: width }}>
        <div className={styles.header}>
          <h3 id={titleId} className={styles.title}>{title}</h3>
          <button className={styles.close} onClick={onClose} aria-label="Close"><CloseIcon size={16} /></button>
        </div>
        <div className={styles.body}>{children}</div>
      </div>
    </dialog>
  );
}
